// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — Sale Form Page
// ─────────────────────────────────────────────────────────────────────────────
// Enhanced with:
//   · Seller field — ComboSelect of all org profiles, auto-populated with logged-in user
//   · Customer field — ComboSelect with debounced search + inline creation
//   · Customer auto-populate panel — shows phone/address on selection
//   · DatePicker — premium calendar for sale date
//   · customer_name snapshot — sent with payload for receipt preservation
// ─────────────────────────────────────────────────────────────────────────────

import { Input, Textarea } from '@/components/ui/FormControls';
import { useDeliveryContract } from '@/hooks/useDeliveries';
import { fulfillmentOptions, type FulfillmentMethod } from '@/lib/deliveries';
import { saleRecipientFormValues, saleRecipientPayload } from '@/lib/saleRecipientSnapshot';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDebounce } from '@/hooks/useDebounce';

import PageHeader from '@/components/common/PageHeader';
import ErrorState from '@/components/common/ErrorState';
import SalePaymentsEditor, {
  emptyPaymentChanges,
  projectedCollected,
  type RecordedSalePayment,
} from '@/components/sales/SalePaymentsEditor';
import {
  salePaymentDraftSchema,
  saleCurrencySchema,
  localBusinessDate,
  paymentRequestKey,
  optionalCustomerPhoneSchema,
} from '@klothiq/validation';
import { useCashAccounts } from '@/hooks/useCash';
import { useConfirm } from '@/contexts/ConfirmContext';
import { allowedSaleTransitions, saleStatusLabel, canCollectSalePayment } from '@/lib/saleStatus';
import LoadingState from '@/components/common/LoadingState';
import ComboSelect from '@/components/common/ComboSelect';
import DatePicker from '@/components/common/DatePicker';
import QuickAddCustomerModal from '@/components/sales/QuickAddCustomerModal';
import VariantPickerCombobox, {
  type CatalogVariant,
} from '@/components/sales/VariantPickerCombobox';
import SaleCartList, { type CartItemData } from '@/components/sales/SaleCartList';
import SaleCartSummary from '@/components/sales/SaleCartSummary';

import { useCreateOrUpdateSale, useSale, useVoidSalePayment } from '@/hooks/useSales';
import { useCustomers } from '@/hooks/useCustomers';
import { useOrgProfiles } from '@/hooks/useOrgProfiles';
import { useStaffMembers } from '@/hooks/useStaffMembers';
import { useProducts } from '@/hooks/useProducts';
import { useInventory } from '@/hooks/useInventory';
import { useAuth } from '@/contexts/AuthContext';

import { Loader2, MapPin, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import type { Database } from '@klothiq/domain';

type Customer = Database['public']['Tables']['customers']['Row'];

// ── Schema ────────────────────────────────────────────────────────────────────

const itemSchema = z.object({
  variant_id: z.string().min(1, 'Select a variant'),
  quantity: z.number({ coerce: true }).int().min(1, 'Min 1'),
  unit_price: saleCurrencySchema,
  discount: saleCurrencySchema.default(0),
});

const saleSchema = z.object({
  // Combined seller selection — namespaced "profile:<id>" or "staff:<id>" so a
  // single ComboSelect can offer both sources. Split back out on submit.
  seller_value: z.string().min(1, 'Seller is required'),
  customer_id: z.string().optional(),
  customer_name: z.string().optional(), // snapshot
  customer_mobile: optionalCustomerPhoneSchema, // blank is valid; WhatsApp checks recipient format separately
  customer_address: z.string().optional(), // snapshot — same
  sale_date: salePaymentDraftSchema.shape.payment_date,
  sale_status: z.string().default('draft'),
  fulfillment_method: z
    .enum(['unspecified', 'courier', 'local_delivery', 'pickup'])
    .default('unspecified'),
  delivery_charge: saleCurrencySchema.default(0),
  notes: z.string().optional(),
  items: z.array(itemSchema).min(1, 'Add at least one item to the sale'),
});

type SaleFormValues = z.infer<typeof saleSchema>;

// ── Shared Styles ─────────────────────────────────────────────────────────────

const inputCls =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground';

// ── Field Wrapper ─────────────────────────────────────────────────────────────

function Field({
  id,
  label,
  error,
  required,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── STATUS OPTIONS ─────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', sublabel: 'Saved, not reserved' },
  { value: 'booked', label: 'Booked', sublabel: 'Stock reserved' },
  { value: 'confirmed', label: 'Confirmed', sublabel: 'Ready for dispatch' },
  { value: 'delivery_pending', label: 'Delivery Pending', sublabel: 'Delivery in progress' },
  { value: 'delivered', label: 'Delivered', sublabel: 'Fulfilled & Delivered immediately' },
];

// ── Main Component ─────────────────────────────────────────────────────────────

export default function SaleFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, role, organization, can } = useAuth();
  const isEditing = !!id;
  const isSellerLocked = role === 'staff';
  const today = localBusinessDate(organization?.timezone);
  const confirm = useConfirm();
  const request = useRef({ signature: '', key: '' });
  const [paymentChanges, setPaymentChanges] = useState(() =>
    emptyPaymentChanges(organization?.timezone),
  );
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [confirmedDuplicate, setConfirmedDuplicate] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const voidPayment = useVoidSalePayment();
  const { data: accounts = [], error: accountsError } = useCashAccounts();

  // ── Data hooks ──
  const { data: existing, isLoading: loadingExisting, error: existingError, refetch } = useSale(id);
  const initial = useRef<typeof existing>();
  const previousId = useRef(id);
  const { data: orgProfiles = [] } = useOrgProfiles();
  const { data: staffMembers = [] } = useStaffMembers();
  const { data: products } = useProducts();
  const { data: inventory } = useInventory();
  const createOrUpdate = useCreateOrUpdateSale();
  const deliveryContract = useDeliveryContract();

  // ── Customer search state ──
  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedSearch = useDebounce(customerSearch, 300);
  const { data: customers = [], isFetching: customersLoading } = useCustomers(
    debouncedSearch.length >= 2 ? debouncedSearch : undefined,
  );

  // ── Selected customer for auto-populate panel ──
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // ── QuickAdd modal state ──
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');

  // ── RHF setup ──
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    setError: setFormError,
    formState: { errors, isSubmitting },
  } = useForm<SaleFormValues>({
    resolver: zodResolver(saleSchema),
    defaultValues: {
      seller_value: profile?.id ? `profile:${profile.id}` : '',
      customer_id: '',
      customer_name: '',
      customer_mobile: '',
      customer_address: '',
      sale_date: today,
      sale_status: 'draft',
      delivery_charge: 0,
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const watchItems = watch('items');

  useEffect(() => {
    if (previousId.current !== id) {
      previousId.current = id;
      initial.current = undefined;
      request.current = { signature: '', key: '' };
      setPaymentChanges(emptyPaymentChanges(organization?.timezone));
      setPaymentError(null);
      setConfirmedDuplicate(false);
      setSelectedCustomer(null);
      reset({
        seller_value: profile?.id ? `profile:${profile.id}` : '',
        customer_id: '',
        customer_name: '',
        customer_mobile: '',
        customer_address: '',
        sale_date: today,
        sale_status: 'draft',
        delivery_charge: 0,
        notes: '',
        items: [],
      });
    }
  }, [id, profile?.id, organization?.timezone, today, reset]);

  // ── Populate seller with logged-in profile on mount ──
  useEffect(() => {
    if (profile?.id && !isEditing) {
      setValue('seller_value', `profile:${profile.id}`);
    }
  }, [profile?.id, isEditing, setValue]);

  // ── FEAT-SALE-EDIT-002: Role guard — redirect viewer away from edit mode ──
  useEffect(() => {
    if (isEditing && role === 'viewer') {
      navigate(`/sales/${id}`, { replace: true });
      toast.info('You do not have permission to edit sales.');
    }
  }, [isEditing, role, id, navigate]);

  // ── FEAT-SALE-EDIT-002: Status guard — redirect if sale is not editable ──
  useEffect(() => {
    if (isEditing && existing && (existing as { is_voided?: boolean }).is_voided) {
      navigate(`/sales/${id}`, { replace: true });
      toast.error('Voided sales cannot be edited.');
    }
  }, [isEditing, existing, id, navigate]);

  // ── Load existing sale for editing ──
  useEffect(() => {
    if (existing && initial.current?.id !== existing.id) {
      initial.current = existing;
      setPaymentChanges(emptyPaymentChanges(organization?.timezone));
      const e = existing as typeof existing & {
        sale_items: Array<{
          variant_id: string;
          quantity: number;
          unit_price: number;
          discount: number;
        }>;
        customers: Customer | null;
        sold_by?: string | null;
        sold_by_staff_id?: string | null;
        customer_mobile?: string | null;
        customer_address?: string | null;
        fulfillment_method?: FulfillmentMethod;
      };
      const sellerValue = e.sold_by_staff_id
        ? `staff:${e.sold_by_staff_id}`
        : e.sold_by
          ? `profile:${e.sold_by}`
          : profile?.id
            ? `profile:${profile.id}`
            : '';
      reset({
        seller_value: sellerValue,
        ...saleRecipientFormValues(e),
        sale_date: e.sale_date,
        sale_status: e.sale_status,
        fulfillment_method: e.fulfillment_method ?? 'unspecified',
        delivery_charge: Number(e.delivery_charge),
        notes: e.notes ?? '',
        items:
          e.sale_items?.map(
            (si: {
              variant_id: string;
              quantity: number;
              unit_price: number | string;
              discount: number | string;
            }) => ({
              variant_id: si.variant_id,
              quantity: si.quantity,
              unit_price: Number(si.unit_price),
              discount: Number(si.discount),
            }),
          ) ?? [],
      });
      if (e.customers) {
        setSelectedCustomer(e.customers);
      }
    }
  }, [existing, reset, profile?.id, reloadVersion]);

  const original = initial.current ?? existing;
  const originalPayments = (original?.payments ?? []) as unknown as RecordedSalePayment[];
  const stockEditable =
    !isEditing ||
    ['draft', 'booked', 'confirmed', 'delivery_pending'].includes(original?.sale_status ?? 'draft');
  const deliveryManaged =
    isEditing &&
    ['courier', 'local_delivery'].includes(
      (original as { fulfillment_method?: string } | undefined)?.fulfillment_method || '',
    );
  const statusOptions =
    isEditing && original
      ? [
          original.sale_status,
          ...(can('sales.status')
            ? allowedSaleTransitions(original.sale_status, role).filter(
                (status) =>
                  !deliveryManaged ||
                  (['draft', 'booked', 'confirmed'].includes(original.sale_status) &&
                    ['booked', 'confirmed'].includes(status)),
              )
            : []),
        ].map((value) => ({ value, label: saleStatusLabel(value) }))
      : STATUS_OPTIONS;

  // ── Customer auto-populate on selection ──
  const handleCustomerChange = useCallback(
    (customerId: string | undefined) => {
      setValue('customer_id', customerId ?? '');
      if (!customerId) {
        setValue('customer_name', '');
        setValue('customer_mobile', '');
        setValue('customer_address', '');
        setSelectedCustomer(null);
        return;
      }
      const found = customers.find((c) => c.id === customerId);
      if (found) {
        setValue('customer_name', found.name);
        setValue('customer_mobile', found.phone ?? '');
        setValue(
          'customer_address',
          [found.address, found.city, found.pincode].filter(Boolean).join(', '),
        );
        setSelectedCustomer(found);
      }
    },
    [customers, setValue],
  );

  // ── Quick add customer inline ──
  const handleCreateCustomer = useCallback((searchTerm: string) => {
    setQuickAddName(searchTerm);
    setQuickAddOpen(true);
  }, []);

  const handleQuickAddSuccess = useCallback(
    (newCustomer: Customer) => {
      setValue('customer_id', newCustomer.id);
      setValue('customer_name', newCustomer.name);
      setValue('customer_mobile', newCustomer.phone ?? '');
      setValue(
        'customer_address',
        [newCustomer.address, newCustomer.city, newCustomer.pincode].filter(Boolean).join(', '),
      );
      setSelectedCustomer(newCustomer);
      setCustomerSearch('');
    },
    [setValue],
  );

  // ── Build options — combined Seller list (system users + staff) ──
  const sellerOptions = [
    ...orgProfiles.map((p) => ({
      value: `profile:${p.id}`,
      label: p.full_name,
      sublabel: 'System User',
    })),
    ...staffMembers.map((s) => ({
      value: `staff:${s.id}`,
      label: s.name,
      sublabel: s.role_label ?? 'Staff',
    })),
  ];
  const sellerLabelByValue = new Map(sellerOptions.map((o) => [o.value, o.label]));

  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: c.name,
    sublabel: c.phone ?? undefined,
  }));

  // ── Existing items map to credit back reserved stock when editing a booked sale ──
  const existingItemsMap = useMemo(() => {
    const map = new Map<string, number>();
    if (
      isEditing &&
      existing &&
      ['booked', 'confirmed', 'delivery_pending'].includes(
        (existing as { sale_status: string }).sale_status,
      )
    ) {
      const items =
        (existing as { sale_items?: Array<{ variant_id: string; quantity: number }> }).sale_items ||
        [];
      for (const it of items) {
        map.set(it.variant_id, (map.get(it.variant_id) || 0) + Number(it.quantity || 0));
      }
    }
    return map;
  }, [isEditing, existing]);

  // ── Variant / inventory data ──
  const catalogVariants: CatalogVariant[] = (products ?? []).flatMap((p) => {
    const invMap = new Map((inventory ?? []).map((i) => [i.variant_id, i]));
    return (p.product_variants ?? []).map((v) => {
      const inv = invMap.get(v.id);
      const rawAvailable = inv?.quantity_available ?? 0;
      // In edit mode for booked orders, add back the quantity this sale already has reserved
      const alreadyReserved = existingItemsMap.get(v.id) || 0;
      const available = Math.max(0, rawAvailable + alreadyReserved);
      return {
        id: v.id,
        productId: p.id,
        productName: p.name,
        productCode: p.product_code,
        team: p.team,
        sku: v.sku,
        size: v.size,
        color: v.color,
        defaultPrice: Number(v.default_price),
        available,
      };
    });
  });

  const variantsMap = new Map(catalogVariants.map((v) => [v.id, v]));
  for (const item of original?.sale_items ?? []) {
    if (!variantsMap.has(item.variant_id)) {
      const variant = item.product_variants;
      variantsMap.set(item.variant_id, {
        id: item.variant_id,
        productId: '',
        productName: variant?.products?.name ?? 'Recorded product',
        productCode: variant?.products?.product_code ?? '',
        team: null,
        sku: variant?.sku ?? '',
        size: variant?.size ?? '',
        color: variant?.color ?? '',
        defaultPrice: Number(item.unit_price),
        available: item.quantity,
      });
    }
  }

  // ── Totals ──
  const grossSubtotal = (watchItems ?? []).reduce(
    (sum, item) => sum + Number(item.unit_price || 0) * Number(item.quantity || 0),
    0,
  );
  const totalDiscount = (watchItems ?? []).reduce(
    (sum, item) => sum + Number(item.discount || 0),
    0,
  );
  const deliveryCharge = Number(watch('delivery_charge') ?? 0);
  const total =
    Math.round((Math.max(0, grossSubtotal - totalDiscount) + deliveryCharge) * 100) / 100;

  const hasInvalidDiscount = (watchItems ?? []).some(
    (item) =>
      Number(item.discount || 0) > Number(item.unit_price || 0) * Number(item.quantity || 1),
  );

  const [highlightedVariantId, setHighlightedVariantId] = useState<string | null>(null);

  // ── Select variant from picker (with duplicate detection) ──
  const handleSelectVariant = useCallback(
    (variant: CatalogVariant) => {
      const currentItems = watchItems ?? [];
      const existingIndex = currentItems.findIndex((it) => it.variant_id === variant.id);

      if (existingIndex >= 0) {
        const existing = currentItems[existingIndex];
        const newQty = (existing?.quantity ?? 1) + 1;
        if (newQty <= variant.available) {
          setValue(`items.${existingIndex}.quantity`, newQty, { shouldValidate: true });
          setHighlightedVariantId(variant.id);
          setTimeout(() => setHighlightedVariantId(null), 600);
        } else {
          toast.warning(
            `Only ${variant.available} available in stock for ${variant.productName} (${variant.size}).`,
          );
        }
      } else {
        append({
          variant_id: variant.id,
          quantity: 1,
          unit_price: variant.defaultPrice,
          discount: 0,
        });
        setHighlightedVariantId(variant.id);
        setTimeout(() => setHighlightedVariantId(null), 600);
      }
    },
    [watchItems, setValue, append],
  );

  // ── Update individual cart row ──
  const handleUpdateCartItem = useCallback(
    (idx: number, updated: Partial<CartItemData>) => {
      if (updated.quantity !== undefined) {
        setValue(`items.${idx}.quantity`, updated.quantity, { shouldValidate: true });
      }
      if (updated.unit_price !== undefined) {
        setValue(`items.${idx}.unit_price`, updated.unit_price, { shouldValidate: true });
      }
      if (updated.discount !== undefined) {
        setValue(`items.${idx}.discount`, updated.discount, { shouldValidate: true });
      }
    },
    [setValue],
  );

  // ── Submit ──
  const onSubmit = async (values: SaleFormValues) => {
    if (
      deliveryContract.data?.version === 1 &&
      !isEditing &&
      values.fulfillment_method === 'unspecified'
    ) {
      setFormError('fulfillment_method', {
        message: 'Choose Courier, Local delivery or Customer pickup.',
      });
      document.getElementById('sale-delivery-method')?.focus();
      return;
    }
    // Stock validation
    for (const item of values.items) {
      const variantInfo = variantsMap.get(item.variant_id);
      if (stockEditable && variantInfo && !['draft', 'cancelled'].includes(values.sale_status)) {
        if (variantInfo.available <= 0) {
          toast.error(`"${variantInfo.productName} (${variantInfo.size})" is out of stock.`);
          return;
        }
        if (item.quantity > variantInfo.available) {
          toast.error(
            `Requested qty (${item.quantity}) exceeds stock (${variantInfo.available}) for ${variantInfo.productName} (${variantInfo.size}).`,
          );
          return;
        }
      }
    }

    const { seller_value } = values;
    let soldBy: string | undefined;
    let soldByStaffId: string | undefined;

    if (seller_value?.startsWith('profile:')) {
      soldBy = seller_value.slice('profile:'.length);
    } else if (seller_value?.startsWith('staff:')) {
      soldByStaffId = seller_value.slice('staff:'.length);
    } else if (staffMembers.some((s) => s.id === seller_value)) {
      soldByStaffId = seller_value;
    } else if (
      orgProfiles.some((p) => p.id === seller_value) ||
      (profile?.id && profile.id === seller_value)
    ) {
      soldBy = seller_value;
    } else if (profile?.id) {
      soldBy = profile.id;
    }

    if (!soldBy && !soldByStaffId) {
      toast.error('A seller is required. Please select a seller.');
      return;
    }

    const sellerName =
      sellerLabelByValue.get(seller_value) ||
      orgProfiles.find((p) => p.id === soldBy)?.full_name ||
      staffMembers.find((s) => s.id === soldByStaffId)?.name ||
      profile?.full_name ||
      undefined;

    const recipient = saleRecipientPayload(values, isEditing ? original : undefined);
    const cleanNotes = values.notes?.trim() || undefined;
    const cleanDeliveryCharge = Number(values.delivery_charge) || 0;

    const cleanItems = (values.items || []).map((it) => ({
      variant_id: it.variant_id.trim(),
      quantity: Math.max(1, Math.floor(Number(it.quantity) || 1)),
      unit_price: Math.max(0, Number(it.unit_price) || 0),
      discount: Math.max(0, Number(it.discount) || 0),
    }));

    if (cleanItems.length === 0) {
      toast.error('Add at least one item to the sale.');
      return;
    }

    setPaymentError(null);
    const edits = [];
    for (const correction of paymentChanges.edits) {
      const checked = salePaymentDraftSchema.safeParse(correction);
      if (!checked.success) {
        setPaymentError(checked.error.issues[0]?.message ?? 'Check payment details');
        return;
      }
      const old = originalPayments.find((p) => p.id === correction.payment_id);
      if (!old) {
        setPaymentError('The selected payment is no longer available. Reload the sale.');
        return;
      }
      if (
        (Number(old.amount) !== checked.data.amount ||
          old.account_id !== checked.data.account_id) &&
        (checked.data.reason?.length ?? 0) < 3
      ) {
        setPaymentError(
          'Enter a correction reason of at least 3 characters for an amount or account change.',
        );
        return;
      }
      edits.push({
        ...checked.data,
        payment_id: correction.payment_id,
        expected_updated_at: correction.expected_updated_at,
      });
    }
    const collected = projectedCollected(originalPayments, edits);
    const remaining = Math.round((total - collected) * 100) / 100;
    if (collected < 0 || remaining < 0) {
      setPaymentError(
        'Collected payments must be between ₹0 and the sale total. Correct payments before reducing the total.',
      );
      return;
    }
    let payment;
    if (paymentChanges.enabled) {
      if (!canCollectSalePayment(values.sale_status)) {
        setPaymentError('Choose an eligible fulfillment status before collecting payment.');
        return;
      }
      const checked = salePaymentDraftSchema.safeParse({
        ...paymentChanges.payment,
        amount: paymentChanges.partial ? paymentChanges.payment.amount : remaining,
      });
      if (!checked.success) {
        setPaymentError(checked.error.issues[0]?.message ?? 'Check payment details');
        return;
      }
      if (checked.data.amount > remaining) {
        setPaymentError('This payment exceeds the remaining balance.');
        return;
      }
      let duplicateConfirmed = confirmedDuplicate;
      const duplicate = originalPayments.some(
        (p) =>
          !p.is_void &&
          p.payment_type === 'sale_payment' &&
          Number(p.amount) === checked.data.amount &&
          p.account_id === checked.data.account_id &&
          p.payment_date === checked.data.payment_date &&
          p.payment_method === checked.data.payment_method,
      );
      if (duplicate && !duplicateConfirmed) {
        duplicateConfirmed = await confirm({
          title: 'Similar payment already recorded',
          description:
            'A collection with the same amount, account, date and method already exists. Add this only if the customer made a separate payment.',
          confirmText: 'Add separate payment',
          variant: 'warning',
        });
        if (!duplicateConfirmed) return;
        setConfirmedDuplicate(true);
      }
      payment = {
        ...checked.data,
        collect_remaining: !paymentChanges.partial,
        duplicate_confirmed: duplicateConfirmed,
      };
    }
    try {
      const payload = {
        ...(deliveryContract.data?.version === 1
          ? { fulfillment_method: values.fulfillment_method }
          : {}),
        sale_id: isEditing ? id : undefined,
        // FEAT-SALE-EDIT-002: send expected_updated_at for optimistic concurrency
        expected_updated_at: isEditing && original ? original.updated_at : undefined,
        sold_by: soldBy,
        sold_by_staff_id: soldByStaffId,
        seller_name: sellerName,
        ...recipient,
        sale_date: values.sale_date,
        sale_status: values.sale_status,
        delivery_charge: cleanDeliveryCharge,
        notes: cleanNotes,
        items: cleanItems,
        payment,
        payment_edits: edits,
      };
      const signature = JSON.stringify(payload);
      if (request.current.signature !== signature)
        request.current = { signature, key: paymentRequestKey() };
      const result = await createOrUpdate.mutateAsync({
        ...payload,
        request_key: request.current.key,
      });
      toast.success(isEditing ? 'Sale updated successfully.' : 'Sale created');
      navigate(`/sales/${result.sale_id}`);
    } catch (err: unknown) {
      console.error('Failed to save sale:', err);
      const postgrestErr = err as {
        message?: string;
        details?: string;
        hint?: string;
        code?: string;
      };
      const errorMessage =
        postgrestErr?.message || (err instanceof Error ? err.message : 'Failed to save sale');

      // FEAT-SALE-EDIT-002: enhanced error mapping
      setPaymentError(errorMessage);
      if (errorMessage.includes('POSSIBLE_DUPLICATE')) {
        const accepted = await confirm({
          title: 'Similar collection found',
          description:
            'Another matching collection exists. Confirm only if this is a separate payment, then save again.',
          confirmText: 'Allow separate payment',
          variant: 'warning',
        });
        setConfirmedDuplicate(accepted);
      } else if (
        errorMessage.includes('modified by another user') ||
        errorMessage.includes('changed since')
      ) {
        toast.warning(errorMessage, {
          action: { label: 'Reload', onClick: () => window.location.reload() },
        });
      } else if (errorMessage.includes('Insufficient stock')) {
        toast.error(errorMessage);
      } else if (errorMessage.includes('Cannot reduce quantity')) {
        toast.error(errorMessage);
      } else if (errorMessage.includes('Cannot reduce total')) {
        toast.error(errorMessage);
      } else if (
        errorMessage.includes('cannot be edited') ||
        errorMessage.includes('Voided sales')
      ) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  if (loadingExisting && isEditing) return <LoadingState message="Loading sale…" />;
  if (existingError && isEditing)
    return (
      <ErrorState
        message="Unable to load this sale. Your access or connection may have changed."
        onRetry={() => void refetch()}
      />
    );
  const isWorking =
    isSubmitting ||
    createOrUpdate.isPending ||
    voidPayment.isPending ||
    (deliveryContract.fetchStatus === 'fetching' && !deliveryContract.data);

  return (
    <>
      <div>
        <PageHeader
          title={isEditing ? 'Edit Sale' : 'New Sale Order'}
          description="Create or update a sale order"
          backTo="/sales"
        />

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5 mx-auto max-w-3xl">
          <fieldset disabled={isWorking} className="min-w-0 space-y-5">
            {/* ── Order Details Card ── */}
            <div className="obsidian-surface rounded-lg border border-border bg-card p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-semibold text-foreground">Order Details</h2>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Seller field — combined system-user + staff list. Locked to self for staff role. */}
                <Field
                  id="sale-seller"
                  label="Seller"
                  required
                  error={errors.seller_value?.message}
                >
                  <Controller
                    name="seller_value"
                    control={control}
                    render={({ field: f }) => (
                      <ComboSelect
                        id="sale-seller"
                        value={f.value || undefined}
                        onChange={(v) => f.onChange(v ?? '')}
                        options={sellerOptions}
                        placeholder="Select seller…"
                        searchPlaceholder="Search staff…"
                        clearable={!isSellerLocked}
                        disabled={isSellerLocked}
                        required
                        hasError={!!errors.seller_value}
                        aria-label="Seller"
                      />
                    )}
                  />
                </Field>

                {/* Sale Date */}
                <Field id="sale-date" label="Sale Date" required error={errors.sale_date?.message}>
                  <Controller
                    name="sale_date"
                    control={control}
                    render={({ field: f }) => (
                      <DatePicker
                        id="sale-date"
                        aria-label="Sale date"
                        ref={f.ref}
                        name={f.name}
                        onBlur={f.onBlur}
                        today={today}
                        clearable={false}
                        disabled={isWorking}
                        value={f.value || undefined}
                        onChange={(v) => f.onChange(v ?? '')}
                        hasError={!!errors.sale_date}
                      />
                    )}
                  />
                </Field>

                {/* Customer ComboSelect */}
                <Field id="sale-customer" label="Customer" error={errors.customer_id?.message}>
                  <Controller
                    name="customer_id"
                    control={control}
                    render={({ field: f }) => (
                      <ComboSelect
                        id="sale-customer"
                        value={f.value || undefined}
                        onChange={handleCustomerChange}
                        options={customerOptions}
                        placeholder="Walk-in / Search customer…"
                        searchPlaceholder="Type name or phone…"
                        isLoading={customersLoading}
                        onSearch={setCustomerSearch}
                        onCreateOption={handleCreateCustomer}
                        createLabel={(term) => `Create new customer "${term}"`}
                        clearable
                        hasError={!!errors.customer_id}
                        helpLabel="Customer"
                      />
                    )}
                  />
                </Field>

                {/* Status */}
                {deliveryContract.isError && (
                  <p className="text-xs text-muted-foreground">
                    Delivery setup is pending. This sale can still be saved and classified in
                    Delivery after the database update.
                  </p>
                )}
                {deliveryContract.data?.version === 1 && (
                  <Field
                    id="sale-delivery-method"
                    label="Delivery method"
                    required={!isEditing}
                    error={errors.fulfillment_method?.message}
                  >
                    <Controller
                      name="fulfillment_method"
                      control={control}
                      rules={{
                        validate: (value) =>
                          isEditing ||
                          value !== 'unspecified' ||
                          'Choose how this sale reaches the customer',
                      }}
                      render={({ field }) => (
                        <ComboSelect
                          id="sale-delivery-method"
                          value={field.value === 'unspecified' ? undefined : field.value}
                          onChange={(value) => field.onChange(value ?? 'unspecified')}
                          options={fulfillmentOptions}
                          clearable={false}
                          placeholder="Courier, local delivery or pickup"
                          helpLabel="Delivery method"
                        />
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      Courier and Local delivery appear automatically in Delivery. Pickup stays in
                      Sales.
                    </p>
                  </Field>
                )}
                <Field id="sale-status" label="Status" error={errors.sale_status?.message}>
                  {deliveryManaged && (
                    <p className="text-xs text-muted-foreground">
                      Packing, dispatch, delivery and corrections are managed from the Delivery
                      menu.
                    </p>
                  )}
                  <Controller
                    name="sale_status"
                    control={control}
                    render={({ field: f }) => (
                      <ComboSelect
                        id="sale-status"
                        value={f.value}
                        onChange={(v) => f.onChange(v ?? 'draft')}
                        options={statusOptions}
                        placeholder="Select status…"
                        clearable={false}
                        hasError={!!errors.sale_status}
                        helpLabel="Status"
                      />
                    )}
                  />
                </Field>

                <Field
                  id="sale-customer-name"
                  label="Customer name on receipt"
                  error={errors.customer_name?.message}
                >
                  <Input
                    id="sale-customer-name"
                    {...register('customer_name')}
                    className={inputCls}
                    placeholder="Walk-in or customer name"
                    helpLabel="Customer name on receipt"
                  />
                </Field>

                {/* Delivery Charge */}
                <Field
                  id="sale-delivery"
                  label="Delivery Charge (₹)"
                  error={errors.delivery_charge?.message}
                >
                  <Controller
                    name="delivery_charge"
                    control={control}
                    render={({ field: f }) => (
                      <Input
                        id="sale-delivery"
                        type="number"
                        min={0}
                        step="0.01"
                        value={f.value}
                        onChange={(e) => f.onChange(parseFloat(e.target.value) || 0)}
                        className={inputCls}
                        helpLabel="Delivery Charge (₹)"
                      />
                    )}
                  />
                </Field>
              </div>

              {/* Customer contact — auto-filled + editable on selection, or manual entry for a walk-in sale */}
              <div className="mt-4 rounded-md border border-border/60 bg-muted/20 p-3">
                <div className="mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <p className="text-sm font-semibold text-foreground">
                    {selectedCustomer ? selectedCustomer.name : 'Walk-in customer'}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field
                    id="sale-customer-mobile"
                    label="Customer Mobile (optional)"
                    error={errors.customer_mobile?.message}
                  >
                    <div className="relative">
                      <Phone className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="sale-customer-mobile"
                        type="tel"
                        {...register('customer_mobile')}
                        placeholder="Mobile number"
                        className={`${inputCls} pl-8`}
                        helpLabel="Customer Mobile (optional)"
                      />
                    </div>
                  </Field>
                  <Field
                    id="sale-customer-address"
                    label="Customer Address"
                    error={errors.customer_address?.message}
                  >
                    <div className="relative">
                      <MapPin className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="sale-customer-address"
                        type="text"
                        {...register('customer_address')}
                        placeholder="Address"
                        className={`${inputCls} pl-8`}
                        helpLabel="Customer Address"
                      />
                    </div>
                  </Field>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-4">
                <Field id="sale-notes" label="Notes" error={errors.notes?.message}>
                  <Textarea
                    id="sale-notes"
                    {...register('notes')}
                    rows={2}
                    placeholder="Delivery instructions or remarks…"
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    helpLabel="Notes"
                  />
                </Field>
              </div>
            </div>

            {/* ── Items Card (POS Cart) ── */}
            <div className="obsidian-surface rounded-lg border border-border bg-card p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">
                    Sale Items <span className="text-destructive">*</span>
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Search and add products to build the sale cart
                  </p>
                </div>
                {fields.length > 0 && (
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                    {fields.length} {fields.length === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>

              {!stockEditable && (
                <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  Product quantities are locked after fulfillment. Prices, discounts, customer
                  details and payments can be corrected. Use the return workflow for stock changes.
                </p>
              )}
              {/* Persistent Variant Search Combobox */}
              <VariantPickerCombobox
                variants={catalogVariants}
                onSelectVariant={handleSelectVariant}
                disabled={isWorking || !stockEditable || role === 'viewer'}
              />

              {errors.items?.root && (
                <p className="text-xs text-destructive">{errors.items.root.message}</p>
              )}
              {errors.items?.message && (
                <p className="text-xs text-destructive">{errors.items.message}</p>
              )}

              {/* Multi-Item Cart List */}
              <SaleCartList
                items={watchItems ?? []}
                variantsMap={variantsMap}
                onUpdateItem={handleUpdateCartItem}
                onRemoveItem={(idx) => remove(idx)}
                highlightedVariantId={highlightedVariantId}
                lockQuantities={!stockEditable}
                disabled={isWorking || role === 'viewer'}
              />
              {fields.map((field, index) => {
                const row = errors.items?.[index];
                const message =
                  row?.quantity?.message || row?.unit_price?.message || row?.discount?.message;
                return message ? (
                  <p key={field.id} role="alert" className="text-sm text-destructive">
                    Item {index + 1}: {message}
                  </p>
                ) : null;
              })}

              {/* Live Cart Financial Summary */}
              {fields.length > 0 && (
                <SaleCartSummary
                  subtotal={grossSubtotal}
                  discount={totalDiscount}
                  deliveryCharge={deliveryCharge}
                  total={total}
                />
              )}
            </div>

            <div id="payments" className="scroll-mt-20">
              <SalePaymentsEditor
                value={paymentChanges}
                onChange={(next) => {
                  setPaymentChanges(next);
                  setPaymentError(null);
                  setConfirmedDuplicate(false);
                }}
                payments={originalPayments}
                accounts={accounts}
                total={total}
                eligible={canCollectSalePayment(watch('sale_status'))}
                isEditing={isEditing}
                disabled={isWorking || role === 'viewer'}
                error={paymentError}
                onVoid={
                  isEditing && ['owner', 'admin'].includes(role ?? '')
                    ? async (payment, reason) => {
                        const accepted = await confirm({
                          title: 'Void recorded payment?',
                          description:
                            'This reverses the payment and its linked cash entry. Unsaved sale edits will be discarded. The original entry remains in history.',
                          confirmText: 'Void payment',
                          variant: 'destructive',
                        });
                        if (!accepted) return;
                        const payload = {
                          payment_id: payment.id,
                          sale_id: id!,
                          expected_updated_at: payment.updated_at,
                          reason,
                        };
                        const signature = JSON.stringify(payload);
                        if (request.current.signature !== signature)
                          request.current = { signature, key: paymentRequestKey() };
                        try {
                          await voidPayment.mutateAsync({
                            ...payload,
                            request_key: request.current.key,
                          });
                          await refetch();
                          initial.current = undefined;
                          setReloadVersion((version) => version + 1);
                          setPaymentChanges(emptyPaymentChanges(organization?.timezone));
                          setPaymentError(null);
                          toast.success('Payment voided');
                        } catch (err) {
                          setPaymentError(
                            err instanceof Error ? err.message : 'Unable to void payment',
                          );
                        }
                      }
                    : undefined
                }
              />
              {accountsError && (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  Unable to load receiving accounts. Reload before recording or moving a payment.
                </p>
              )}
            </div>
            {/* ── Actions ── */}
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/sales')}
                className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isWorking || fields.length === 0 || hasInvalidDiscount || role === 'viewer'
                }
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isWorking && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Sale'}
              </button>
            </div>
          </fieldset>
        </form>
      </div>

      {/* QuickAdd Customer Modal */}
      {quickAddOpen && (
        <QuickAddCustomerModal
          initialName={quickAddName}
          onClose={() => setQuickAddOpen(false)}
          onSuccess={handleQuickAddSuccess}
        />
      )}
    </>
  );
}
